"use client";
import React, { useEffect, InputHTMLAttributes, useRef } from "react";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@/components/buttons"; // Assuming Button component is correctly imported
import cn from "@/utils/cn";
import Link from "next/link";
import { auditFormSchema, AuditFormData } from "@/utils/schemas/formSchemas";
import { useFormSubmission } from "@/app/hooks/useFormSubmission";
import { useZStore } from "@/utils/z-store"; // Import useZStore
import { trackGAEvent } from "@/utils/ga";
import TurnstileWidget, { executeTurnstile } from "@/app/_components/TurnstileWidget";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import MailchimpConnectButton from "@/components/MailchimpConnectButton";

import { MdOutlineChevronLeft } from "react-icons/md";

interface LocalInputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
}

const LocalInputField = React.forwardRef<
  HTMLInputElement,
  LocalInputFieldProps
>(({ name, type = "text", placeholder, className, required, ...rest }, ref) => {
  const { register } = useFormContext();
  const { ref: rhfRef, ...registerProps } = register(name);

  return (
    <input
      id={name}
      type={type}
      placeholder={placeholder}
      required={required}
      {...registerProps}
      {...rest}
      ref={(e) => {
        rhfRef(e);
        if (typeof ref === "function") {
          ref(e);
        } else if (ref && typeof ref === "object") {
          ref.current = e;
        }
      }}
      className={cn(
        "h-[40px] grow rounded-lg bg-white px-3 xl:text-sm text-[9.5px] text-gray-900 placeholder:text-gray-500 focus:border-[#1CE783] focus:outline-none transition-colors duration-150 ease-in-out",
        className,
      )}
    />
  );
});
LocalInputField.displayName = "LocalInputField";

type StandaloneAuditFormProps = {
  showApiKey: boolean;
  setShowApiKey: (showApiKey: boolean) => void;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export default function StandaloneAuditForm({
  showApiKey = false,
  setShowApiKey,
  className,
  ...props
}: StandaloneAuditFormProps) {
  const { isLoading, submissionStatus, submitForm, setSubmissionStatus } =
    useFormSubmission();

  const methods = useForm<AuditFormData>({
    resolver: zodResolver(auditFormSchema),
    mode: "onChange",
  });

  const {
    handleSubmit,
    trigger,
    watch,
    setValue,
    reset,
    formState: { errors, touchedFields }, // Added touchedFields
    clearErrors, // Added clearErrors
  } = methods;

  const emailValue = watch("email");
  const apiKeyFieldValue = watch("apiKey");

  const apiKeyInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const isInitialMount = useRef(true);
  const turnstileWidgetRef = useRef<TurnstileInstance | null>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;
    if (submissionStatus.message) {
      timeoutId = setTimeout(() => {
        setSubmissionStatus({ type: null, message: null });
      }, 7000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [submissionStatus.message]); // Keep this useEffect as it's specific to clearing messages after a delay

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!showApiKey) {
        return;
      }
    }
    // Focus management when steps change
    const focusDelay = 150; // Sync with animation or slightly after
    let timer: NodeJS.Timeout;

    if (showApiKey) {
      timer = setTimeout(() => {
        apiKeyInputRef.current?.focus();
      }, focusDelay);
    } else {
      timer = setTimeout(() => {
        emailInputRef.current?.focus();
      }, focusDelay);
    }
    return () => clearTimeout(timer);
  }, [showApiKey]);

  const handleContinue = async () => {
    setSubmissionStatus({ type: null, message: null });
    const isEmailValid = await trigger("email");
    if (isEmailValid) {
      setValue("apiKey", "");
      clearErrors("apiKey"); // Clear any previous API key errors
      setShowApiKey(true);
    }
  };

  const handleGoBack = () => {
    setSubmissionStatus({ type: null, message: null }); // Clear any messages
    setShowApiKey(false);
    setValue("apiKey", ""); // Clear API key value
    clearErrors("apiKey"); // Clear API key errors
    // Focus will be handled by the useEffect for showApiKey
  };

  const { actions } = useZStore(); // Destructure actions from useZStore

  const finalizeAuditSubmission = async (data: AuditFormData) => {
    const turnstileToken = await executeTurnstile(turnstileWidgetRef);
    if (!turnstileToken) {
      setSubmissionStatus({
        type: "error",
        message:
          "Verification failed. Please disable any ad blockers and try again.",
      });
      return false;
    }
    try {
      const { success, trackerId, reportId } = await submitForm(
        { ...data, turnstileToken },
        "EAE_FORM",
        "Report generation started! We'll email you shortly.",
      );
      if (success) {
        trackGAEvent("free_audit_submit", { email: data.email });
        // Canonical funnel event (Tracking tab): top of the value-first funnel.
        trackGAEvent("hero_email_submitted", { email: data.email });
        reset();
        setShowApiKey(false);
        if (trackerId) {
          actions.updateProcessingStatus({
            status: "processing",
            progress: 7,
            trackerId: trackerId,
            reportId: reportId,
          });
        }
      }
      return success;
    } finally {
      turnstileWidgetRef.current?.reset();
    }
  };

  const onSubmit = async (data: AuditFormData) => {
    await finalizeAuditSubmission(data);
  };

  // Called when the Mailchimp OAuth popup returns a token. The OAuth token
  // format does not match the manual-API-key regex, so we bypass RHF
  // validation and submit using the email already entered + the OAuth token.
  const handleOAuthToken = async (token: string) => {
    const isEmailValid = await trigger("email");
    if (!isEmailValid) {
      setSubmissionStatus({ type: "error", message: "Please enter a valid email first." });
      return;
    }
    const email = methods.getValues("email");
    setValue("apiKey", token);
    await finalizeAuditSubmission({ email, apiKey: token });
  };

  const handleOAuthError = (errorCode: string) => {
    let message: string;
    switch (errorCode) {
      case "popup_blocked":
        message = "Pop-up was blocked. Please allow pop-ups and try again.";
        break;
      case "popup_closed":
        message = "Mailchimp connection cancelled.";
        break;
      case "timeout":
        message = "Mailchimp connection timed out. Please try again.";
        break;
      default:
        message = "Mailchimp connection failed. Please try again.";
    }
    setSubmissionStatus({ type: "error", message });
  };

  let topMessage = submissionStatus.message;
  let topMessageType = submissionStatus.type;

  if (!topMessage) {
    if (!showApiKey && errors.email?.message && touchedFields.email) {
      topMessage = errors.email.message;
      topMessageType = "error";
    } else if (showApiKey && errors.apiKey?.message && touchedFields.apiKey) {
      topMessage = errors.apiKey.message;
      topMessageType = "error";
    }
  }

  const animationDuration = "duration-500";
  const animationEasing = "ease-in-out";

  return (
    <div className={cn("w-full", className)} {...props}>
      {topMessage && (
        <div
          className={cn(
            "text-[8px] xl:text-[12px] px-3 mb-1 rounded-md",
            topMessageType === "success" ? "text-[#00E0D3]" : "",
            topMessageType === "error" ? "text-[#FA5A7D]" : "",
          )}
          role="alert"
        >
          {topMessage}
        </div>
      )}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <TurnstileWidget ref={turnstileWidgetRef} />
          <div
            className={cn(
              "bg-white rounded-[12px] xl:rounded-2xl w-full mx-auto xl:w-[576px]",
              className?.includes("xl:w-[") ? "" : className,
            )}
          >
            <div
              className={cn("relative h-[40px] xl:h-[60px] overflow-hidden")}
            >
              {/* Step 1: Email Input */}
              <div
                className={cn(
                  "absolute inset-0 flex items-center space-x-2 px-2 transition-all",
                  animationDuration,
                  animationEasing,
                  showApiKey
                    ? "-translate-y-full opacity-0"
                    : "translate-y-0 opacity-100",
                )}
                aria-hidden={showApiKey}
              >
                <LocalInputField
                  ref={emailInputRef}
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  required
                  tabIndex={showApiKey ? -1 : 0}
                />
                <Button
                  type="button"
                  onClick={handleContinue}
                  disabled={isLoading || !emailValue || !!errors.email}
                  className="h-[30px] shrink-0 cursor-pointer text-nowrap rounded-lg border-2 bg-[#00E0D3] px-2 text-[10.5px] font-medium text-white disabled:text-white disabled:hover:text-[#00E0D3] transition-all duration-200 hover:border-[#00E0D3] hover:border-2 hover:bg-transparent hover:text-[#00E0D3] xl:h-[40px] xl:px-5 xl:text-[16px]"
                  tabIndex={showApiKey ? -1 : 0}
                >
                  Run my free audit
                </Button>
              </div>

              {/* Step 2: API Key Input */}
              <div
                className={cn(
                  "absolute inset-0 flex items-center space-x-2 px-2 transition-all",
                  animationDuration,
                  animationEasing,
                  showApiKey
                    ? "translate-y-0 opacity-100"
                    : "translate-y-full opacity-0",
                )}
                aria-hidden={!showApiKey}
              >
                <button
                  type="button"
                  onClick={handleGoBack}
                  aria-label="Go back to email input"
                  className="shrink-0 rounded-md p-1.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-800 active:bg-gray-200 xl:p-2"
                  tabIndex={!showApiKey ? -1 : 0}
                >
                  <MdOutlineChevronLeft className="size-5 xl:size-6" />
                </button>
                <LocalInputField
                  ref={apiKeyInputRef}
                  name="apiKey"
                  type="text"
                  placeholder="Enter your Mailchimp API key"
                  required
                  tabIndex={!showApiKey ? -1 : 0}
                />
                <Button
                  type="submit"
                  disabled={isLoading || !apiKeyFieldValue || !!errors.apiKey}
                  className="h-[30px] shrink-0 cursor-pointer text-nowrap rounded-lg border-2 bg-[#00E0D3] px-2 text-[10.5px] font-medium text-white disabled:text-white disabled:hover:text-[#00E0D3] transition-all duration-200 hover:border-[#00E0D3] hover:border-2 hover:bg-transparent hover:text-[#00E0D3] xl:h-[40px] xl:px-5 xl:text-[16px]"
                  tabIndex={!showApiKey ? -1 : 0}
                >
                  {isLoading ? "Generating..." : "Generate Report"}
                </Button>
              </div>
            </div>
          </div>
          {showApiKey && (
            <div className="mt-3 flex flex-col items-center gap-2">
              <div className="flex w-full max-w-[300px] items-center gap-2 xl:max-w-[400px]">
                <div className="h-px flex-1 bg-white/30" />
                <span className="text-[9px] font-semibold uppercase tracking-wider text-white/80 xl:text-[11px]">
                  or
                </span>
                <div className="h-px flex-1 bg-white/30" />
              </div>
              <MailchimpConnectButton
                mode="audit"
                withLabel
                disabled={isLoading}
                onTokenReceived={handleOAuthToken}
                onError={handleOAuthError}
                className="h-[30px] px-3 text-[10.5px] xl:h-[40px] xl:px-5 xl:text-[14px]"
              />
              <Link
                href="/data-access"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-white/80 underline underline-offset-2 transition-colors hover:text-white xl:text-[12px]"
              >
                What we access in your Mailchimp account
              </Link>
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
}
